import {
  GetDeviceSnapshotDTO,
  TelemetryHistoryEnrichedDTO,
} from '@eppendorf/vnls-device-service-utils';
import { useEffect, useMemo, useState } from 'react';

import { HistoryChartV2Wrapper } from '$features/monitoring/history-charts-v2/history-chart-v2-wrapper';
import { useDeviceHistoryChart } from '$features/monitoring/history-charts-v2/history-charts.api';
import {
  padHistoryDataToEndOfDay,
  shouldPadHistoryData,
} from '$features/monitoring/history-charts-v2/utils/pad-history-data';

export interface HistoryChartV2Props {
  snapshot: GetDeviceSnapshotDTO;
  parameter: TelemetryHistoryEnrichedDTO;
  from?: number | null;
  to?: number | null;
  liveUpdate: boolean;
  /** Hide setpoint lines and data from the chart */
  hideSetpoints?: boolean;
  /** Override fractional digits for chart values (e.g. 0 for centrifuge-large) */
  fractionalDigits?: number;
}

function useHistoryDateRange(
  from: number | null | undefined,
  to: number | null | undefined,
  liveUpdate: boolean,
): { from: Date; to: Date } {
  const [counter, setCounter] = useState(0);

  const range = useMemo(() => {
    if (liveUpdate) {
      return {
        from: new Date(Date.now() - 1 * 60 * 60 * 1000),
        to: new Date(),
      };
    }

    const fromDate = from ? new Date(from) : new Date(Date.now() - 1 * 60 * 60 * 1000);
    const toDate = to ? new Date(to) : new Date();

    return {
      from: new Date(Math.min(fromDate.getTime(), toDate.getTime())),
      to: new Date(Math.max(fromDate.getTime(), toDate.getTime())),
    };
  }, [from, to, liveUpdate, counter]);

  useEffect(() => {
    if (liveUpdate) {
      const interval = setInterval(() => {
        setCounter((prev) => prev + 1);
      }, 5000); // update every 5 seconds

      return () => clearInterval(interval);
    }
    return undefined;
  }, [liveUpdate]);

  return range;
}

export function HistoryChartV2Container({
  snapshot,
  parameter,
  from,
  to,
  liveUpdate,
  hideSetpoints,
  fractionalDigits,
}: HistoryChartV2Props) {
  const dateRange = useHistoryDateRange(from, to, liveUpdate);
  const { data, isError, isLoading } = useDeviceHistoryChart(
    {
      deviceId: snapshot.inventoryDevice.serialNumber,
    },
    {
      startDate: dateRange.from.toISOString(),
      endDate: dateRange.to.toISOString(),
      telemetryId: parameter.telemetryId,
    },
  );

  const paddedData = useMemo(() => {
    if (!data || liveUpdate || !shouldPadHistoryData(from, to)) {
      return data;
    }
    return padHistoryDataToEndOfDay(data);
  }, [data, liveUpdate, from, to]);

  return (
    <HistoryChartV2Wrapper
      data={paddedData}
      isLoading={isLoading}
      isError={isError}
      parameterName={parameter.name}
      liveUpdate={liveUpdate}
      hideSetpoints={hideSetpoints}
      fractionalDigits={fractionalDigits}
    />
  );
}
